import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebaseService';
import { Loader2, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import type { Coordinates } from '../../../types';

const VEHICLE_LABELS: Record<string, string> = {
  motorhome: 'Bobil',
  campervan: 'Campingbil',
  caravan: 'Campingvogn',
  camper_trailer: 'Tilhenger',
  rooftent: 'Taktelt',
  offroad: 'Terrengkjøretøy',
  car: 'Bil',
};

const SPOT_TYPE_LABELS: Record<string, string> = {
  confirmed: 'Friluftsplass',
  rasteplass: 'Rasteplass',
  commercial_campsite: 'Campingplass',
  coastal: 'Kystplass',
  attraction: 'Attraksjon',
  wildspot: 'Villmark',
  gapahuk: 'Gapahuk',
  osm_amenity: 'Fasiliteter',
  algorithmic: 'Algoritmisk',
  snuplass: 'Snuplass',
};

interface SpotCheckin {
  id: string;
  name: string;
  spotType: string;
  county?: string;
  location?: Coordinates;
  checkInCount: number;
  checkInNights?: number;
  vehicleVisits?: Record<string, number>;
}

interface Props {
  onViewSpot: (spotId: string, location: Coordinates) => void;
}

const AdminCheckinsDashboard: React.FC<Props> = ({ onViewSpot }) => {
  const [loading, setLoading] = useState(true);
  const [spots, setSpots] = useState<SpotCheckin[]>([]);
  const [expandedCounty, setExpandedCounty] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const q = query(
        collection(db, 'cached_spots'),
        where('checkInCount', '>', 0),
        orderBy('checkInCount', 'desc'),
        limit(500)
      );
      const snap = await getDocs(q);
      const result = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || 'Ukjent plass',
          spotType: data.spotType || 'unknown',
          county: data.county,
          location: data.location,
          checkInCount: data.checkInCount || 0,
          checkInNights: data.checkInNights,
          vehicleVisits: data.vehicleVisits,
        } as SpotCheckin;
      });
      setSpots(result);
    } catch (e) {
      console.error('Failed to load checkins', e);
    } finally {
      setLoading(false);
    }
  };

  const grouped: Record<string, SpotCheckin[]> = {};
  spots.forEach(spot => {
    const key = spot.county || 'Ukjent fylke';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(spot);
  });

  const sortedCounties = Object.entries(grouped).sort(
    ([, a], [, b]) =>
      b.reduce((s, x) => s + x.checkInCount, 0) -
      a.reduce((s, x) => s + x.checkInCount, 0)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-emerald-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-[var(--bg-base)] border border-[var(--border-secondary)] rounded-3xl p-6">
        <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text-primary)] mb-2 flex items-center gap-2">
          <MapPin size={16} className="text-emerald-500" />
          Innsjekkinger per fylke
        </h3>
        <p className="text-2xs text-[var(--text-muted)] mb-6">
          {spots.length} plasser med innsjekk · {sortedCounties.length} fylker
        </p>

        {sortedCounties.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)]">Ingen data funnet.</p>
        ) : (
          <div className="space-y-3">
            {sortedCounties.map(([county, countySpots]) => {
              const totalCheckins = countySpots.reduce((s, x) => s + x.checkInCount, 0);
              const isExpanded = expandedCounty === county;
              return (
                <div key={county} className="border border-[var(--border-secondary)] rounded-2xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-[var(--bg-input)] hover:bg-[var(--bg-hover)] transition-colors"
                    onClick={() => setExpandedCounty(isExpanded ? null : county)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-[var(--text-primary)]">{county}</span>
                      <span className="text-2xs bg-emerald-500/10 text-emerald-500 px-2 py-0.5 rounded-full font-black">
                        {totalCheckins} innsjekk
                      </span>
                      <span className="text-2xs text-[var(--text-muted)]">{countySpots.length} plasser</span>
                    </div>
                    {isExpanded
                      ? <ChevronUp size={14} className="text-[var(--text-muted)]" />
                      : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
                  </button>

                  {isExpanded && (
                    <div className="p-4 space-y-2 bg-[var(--bg-base)]">
                      {countySpots.map(spot => (
                        <div
                          key={spot.id}
                          className={`bg-[var(--bg-input)] p-3 rounded-xl border border-[var(--border-secondary)] ${spot.location ? 'cursor-pointer hover:border-emerald-500/50 transition-colors' : ''}`}
                          onClick={() => spot.location && onViewSpot(spot.id, spot.location)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-bold truncate text-[var(--text-primary)] flex items-center gap-1">
                                {spot.name}
                                {spot.location && <MapPin size={10} className="text-emerald-500 shrink-0" />}
                              </p>
                              <p className="text-2xs text-[var(--text-muted)] mt-0.5">
                                {SPOT_TYPE_LABELS[spot.spotType] || spot.spotType}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs font-black text-emerald-500">{spot.checkInCount} innsjekk</p>
                              {!!spot.checkInNights && (
                                <p className="text-2xs text-[var(--text-muted)]">{spot.checkInNights} netter</p>
                              )}
                            </div>
                          </div>
                          {spot.vehicleVisits && Object.keys(spot.vehicleVisits).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {Object.entries(spot.vehicleVisits)
                                .sort(([, a], [, b]) => b - a)
                                .map(([type, count]) => (
                                  <span key={type} className="text-3xs bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded-full">
                                    {VEHICLE_LABELS[type] || type}: {count}
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCheckinsDashboard;
